import React, { useCallback, useMemo } from 'react';

import useToggle from '@lyearn/core/hooks/useToggle';
import getEnabledItems from '@lyearn/core/utils/array/getEnabledItems';
import { useTranslation } from '@lyearn/i18n';
import { ArrowIosLeftOutline, EditOutline, Link2Fill, MoreHorizontal } from '@lyearn/icons';
import { MenuListItems } from '@lyearn/molecules';
import { cn } from '@lyearn/style';
import { Popover, Stack } from '@lyearn/ui';
import ErrorState, { ErrorBoundary } from '@/components/Error';
import usePageLayout from '@/components/PageLayout/usePageLayout';
import Tabs from '@/components/Tabs';
import * as User from '@/components/User';
import { FeatureFlags, useFeatureFlags } from '@/helper/FeatureFlags';
import useCopyToClipboard from '@/hooks/useCopyToClipboard';
import useNavigation from '@/hooks/useNavigation';
import { useAdminRoute } from '@/pages/corporate/routes/hooks/useAdminRoutes';

import EditProfileModal from '../../../onboarding/views/EditProfile/EditProfileModal';
import {
  renderLearnerCover,
  renderLearnerInfoItem,
  renderLearnerProfilePicture,
} from './components/LearnerInfoWidget/renderers';
import { getLearnerInfoItems } from './components/LearnerInfoWidget/utils';
import useLearnerProfileTabs from './components/LearnerProfileContent/hooks/useLearnerProfileTabs';
import { LEARNER_PROFILE_TABS_CONFIG } from './components/LearnerProfileContent/hooks/useLearnerProfileTabs/utils';
import { overflowWrapAnywhere, tabMobileStylesClassName } from './styles';
import { LearnerWidgetProps } from './types';

const LearnerProfileMobile = (props: LearnerWidgetProps) => {
  const { learner, onAction, isCurrentUser: isSelf } = props;
  const { t } = useTranslation();
  const isAdmin = useAdminRoute();
  const { [FeatureFlags.EnableLearnerProfileJoinedDate]: isJoinedDateVisible } = useFeatureFlags();

  const { activeTab, tabs } = useLearnerProfileTabs({ learnerId: learner.id });
  usePageLayout({ showHeader: false });
  const items = useMemo(
    () =>
      getLearnerInfoItems({
        t,
        learner,
        isSelf,
        isAdmin,
        isMobile: true,
        isJoinedDateVisible,
      }),
    [t, learner, isSelf, isAdmin, isJoinedDateVisible],
  );
  const TabComponent = LEARNER_PROFILE_TABS_CONFIG[activeTab]?.Component;

  const navigation = useNavigation();
  const onBack = useCallback(() => {
    navigation.back();
  }, [navigation]);
  const [isActionMenu, { toggle: toggleActionMenu, on: openActionMenu, off: closeActionMenu }] =
    useToggle();
  const [isEditProfileVisible, { toggle: toggleEditProfile, on: openEditProfile }] = useToggle();
  const { onCopyRoute } = useCopyToClipboard();

  const actionMenuItems = useMemo(
    () =>
      getEnabledItems([
        {
          id: 'edit',
          StartIcon: EditOutline,
          label: t('Common:EditProfile'),
          className: '!ps-12 !py-14',
          enabled: isSelf,
        },
        {
          id: 'copy',
          StartIcon: Link2Fill,
          label: t('Common:Actions.CopyLink'),
          className: '!ps-12 !py-14',
          enabled: true,
        },
      ]),
    [isSelf, t],
  );
  const onActionMenu = useCallback(
    (type: string) => {
      switch (type) {
        case 'edit':
          openEditProfile();
          closeActionMenu();
          break;
        case 'copy':
          onCopyRoute(navigation.asPath);
          closeActionMenu();
          break;
      }
    },
    [openEditProfile, closeActionMenu, onCopyRoute, navigation.asPath],
  );

  return (
    <>
      <Stack className="flex-col overflow-hidden relative">
        <ArrowIosLeftOutline
          className="absolute top-[var(--ly-header-height,0px)] left-24"
          fillColorToken="icon-placeholder"
          height={32}
          width={32}
          onClick={onBack}
        />
        <MoreHorizontal
          className="absolute top-[var(--ly-header-height,0px)] right-24"
          fillColorToken="icon-placeholder"
          height={28}
          width={28}
          onClick={openActionMenu}
        />
        {renderLearnerCover({ ...learner, className: '!h-[15.2rem]' })}
        <Stack className="ps-24 pe-28 flex-col flex w-full">
          <User.Provider className="flex-col" user={learner as any}>
            {renderLearnerProfilePicture(learner)}
            <Stack className="mt-24" direction="col" gapY={12}>
              <Stack direction="col" gapY={4}>
                <User.Name
                  className={overflowWrapAnywhere}
                  color="text-text-primary"
                  variant="heading-m"
                />
              </Stack>
              <Stack className="flex-col" gap={12}>
                {items.map(renderLearnerInfoItem)}
              </Stack>
            </Stack>
          </User.Provider>
        </Stack>
        <Tabs className={cn('mt-32', tabMobileStylesClassName)} tabs={tabs} value={activeTab} />
        <Stack className="h-1 bg-border -mt-1 w-full" />
        <Stack className="mt-20">
          <ErrorBoundary fallback={ErrorState}>
            {TabComponent ? (
              <TabComponent
                classNames="w-full px-12 xs:px-0"
                isCurrentUser={isSelf}
                learner={learner}
                onAction={onAction}
              />
            ) : null}
          </ErrorBoundary>
        </Stack>
      </Stack>
      <Popover open={isActionMenu} onOpenChange={toggleActionMenu}>
        <Popover.Content renderAs="bottom-sheet">
          <MenuListItems
            className="px-8 py-32 space-y-4"
            items={actionMenuItems}
            onAction={onActionMenu}
          />
        </Popover.Content>
      </Popover>
      {isEditProfileVisible ? <EditProfileModal onClose={toggleEditProfile} /> : null}
    </>
  );
};

export default LearnerProfileMobile;
